import { useCallback, useMemo, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import RehypeExternalLinks from "rehype-external-links";
import RemarkGFM from "remark-gfm";
import { ChevronDown, ChevronUp, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Prose } from "@/components/ui/prose";

import { GoogleIcon } from "@/components/icons/google";
import { MetaIcon } from "@/components/icons/meta";

import { cn } from "@/lib/utils";

import type { InsightsRunResponse, StructuredInsight } from "@/schema/brand-os/insights-run";

/**
 * Renders `structured.critical`, `structured.opportunities`, `structured.wins`
 * for both `skill_id` values — same tabs and cards; use `skill_id` only for
 * subtitle wording (`insights` vs action-plan copy).
 *
 * - Empty arrays → per-section empty states.
 * - All arrays empty + non-empty `data_summary` → primary prose fallback + badge.
 * - **All** tab: one spotlight row — first card per bucket, 3-col at `lg` (ref layout).
 *   Other tabs: full grids (2/3 columns) for that bucket’s list.
 * - Raw markdown lives behind "Show full analysis".
 */
export function InsightsResult({
  response,
  headerActions,
}: {
  response: InsightsRunResponse;
  /** e.g. Refresh control — shown in the insights module title row */
  headerActions?: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-[1280px] space-y-5 px-0">
      <ThreeTabBody response={response} headerActions={headerActions} />
      <FullAnalysisToggle content={response.content} />
    </div>
  );
}

// ---------- skill: insights -----------------------------------------------

type InsightsTab = "all" | "critical" | "opportunities" | "wins";
type InsightBucket = "critical" | "opportunity" | "win";

/** Shared chrome so insights + recommendations runs both look identical at the shell level. */
function InsightsRunModuleShell({
  subtitle,
  tabRow,
  headerActions,
  children,
}: {
  subtitle: string;
  tabRow: ReactNode;
  headerActions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
      <div className="border-b border-border/60 bg-card px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="inline-flex items-center gap-2 text-base font-semibold tracking-tight text-foreground">
              <Sparkles className="size-5 shrink-0 text-violet-500" strokeWidth={2} />
              <span>AI insights &amp; recommendations</span>
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{subtitle}</p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:max-w-none lg:w-auto lg:shrink-0">
            <div className="flex flex-wrap items-center justify-end gap-2" role="tablist">
              {tabRow}
            </div>
            {headerActions ? <div className="flex justify-end">{headerActions}</div> : null}
          </div>
        </div>
      </div>
      <div className="space-y-4 p-4 sm:p-5">{children}</div>
    </div>
  );
}

function formatRunSubtitle(r: InsightsRunResponse): string {
  const { critical, opportunities, wins } = r.structured;
  const n = critical.length + opportunities.length + wins.length;
  const secs = (r.elapsed_ms / 1000).toFixed(1);
  const plat =
    r.platforms_available?.length
      ? `${r.platforms_available.length} platform${r.platforms_available.length === 1 ? "" : "s"} (${r.platforms_available.join(", ")})`
      : "your connected platforms";
  const unit =
    r.skill_id === "insights"
      ? `insight${n === 1 ? "" : "s"}`
      : `action item${n === 1 ? "" : "s"}`;
  return `Generated from ${n} ${unit} across ${plat} · Completed in ${secs}s`;
}

function ThreeTabBody({
  response,
  headerActions,
}: {
  response: InsightsRunResponse;
  headerActions?: ReactNode;
}) {
  const [tab, setTab] = useState<InsightsTab>("all");
  const [dismissed, setDismissed] = useState(() => new Set<string>());

  const dismiss = useCallback((key: string) => {
    setDismissed((prev) => new Set(prev).add(key));
  }, []);

  const { critical, opportunities, wins, data_summary } = response.structured;

  const nCritical = critical.length;
  const nOpportunities = opportunities.length;
  const nWins = wins.length;
  const nAll = nCritical + nOpportunities + nWins;

  const cardsEmpty = nAll === 0;
  const hasDataSummary = Boolean(data_summary && data_summary.trim());

  if (cardsEmpty && hasDataSummary) {
    return <DataSummaryFallback summary={data_summary!} />;
  }

  const showCritical = tab === "all" || tab === "critical";
  const showOpportunities = tab === "all" || tab === "opportunities";
  const showWins = tab === "all" || tab === "wins";

  const filterOut = useCallback(
    (items: StructuredInsight[], prefix: InsightBucket) =>
      items.filter((item) => !dismissed.has(cardKey(prefix, item))),
    [dismissed],
  );

  const critF = useMemo(() => filterOut(critical, "critical"), [critical, filterOut]);
  const oppF = useMemo(() => filterOut(opportunities, "opportunity"), [opportunities, filterOut]);
  const winF = useMemo(() => filterOut(wins, "win"), [wins, filterOut]);

  return (
    <InsightsRunModuleShell
      subtitle={formatRunSubtitle(response)}
      headerActions={headerActions}
      tabRow={
        <>
          <InsightsTabPill active={tab === "all"} onClick={() => setTab("all")} label="All" count={nAll} />
          <InsightsTabPill
            active={tab === "critical"}
            onClick={() => setTab("critical")}
            label="Critical"
            count={nCritical}
            variant="critical"
          />
          <InsightsTabPill
            active={tab === "opportunities"}
            onClick={() => setTab("opportunities")}
            label="Opportunities"
            count={nOpportunities}
            variant="opportunity"
          />
          <InsightsTabPill
            active={tab === "wins"}
            onClick={() => setTab("wins")}
            label="Wins"
            count={nWins}
            variant="win"
          />
        </>
      }
    >
      {hasDataSummary && <AnalysisPanel summary={data_summary!} />}

      {tab === "all" ? (
        <>
          <AllTabSpotlightRow
            critF={critF}
            oppF={oppF}
            winF={winF}
            onDismiss={dismiss}
          />
          {(critF.length > 1 || oppF.length > 1 || winF.length > 1) && (
            <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
              More items per category — open Critical, Opportunities, or Wins above.
            </p>
          )}
        </>
      ) : (
        <>
          {showCritical && (
            <InsightsColumn
              tabAll={false}
              sectionLabel="Critical"
              emptyLabel="No critical issues this run."
              items={critF}
              bucket="critical"
              onDismiss={dismiss}
            />
          )}
          {showOpportunities && (
            <InsightsColumn
              tabAll={false}
              sectionLabel="Opportunities"
              emptyLabel="No opportunities to address this run."
              items={oppF}
              bucket="opportunity"
              onDismiss={dismiss}
            />
          )}
          {showWins && (
            <InsightsColumn
              tabAll={false}
              sectionLabel="Wins"
              emptyLabel="No wins to highlight this run."
              items={winF}
              bucket="win"
              onDismiss={dismiss}
            />
          )}
        </>
      )}
    </InsightsRunModuleShell>
  );
}

type TabVariant = "neutral" | "critical" | "opportunity" | "win";

function InsightsTabPill({
  active,
  onClick,
  label,
  count,
  variant = "neutral",
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  variant?: TabVariant;
}) {
  const inactiveRing: Record<TabVariant, string> = {
    neutral: "border-muted-foreground/20 bg-background text-muted-foreground",
    critical: "border-destructive/50 bg-background text-destructive",
    opportunity:
      "border-amber-500/50 bg-background text-amber-600 dark:text-amber-400",
    win: "border-emerald-500/50 bg-background text-emerald-600 dark:text-emerald-400",
  };

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active
          ? "border-foreground/20 bg-foreground text-background"
          : inactiveRing[variant],
      )}
    >
      <span>{label}</span>
      <span className="tabular-nums opacity-90">({count})</span>
    </button>
  );
}

/**
 * Reference layout (dashboard mock): one row × three columns on **All** —
 * first insight per bucket (Critical · Opportunities · Wins), ~⅓ width each
 * at `lg`, ~16–20px gutters (`gap-5`).
 */
function AllTabSpotlightRow({
  critF,
  oppF,
  winF,
  onDismiss,
}: {
  critF: StructuredInsight[];
  oppF: StructuredInsight[];
  winF: StructuredInsight[];
  onDismiss: (key: string) => void;
}) {
  const slots: {
    bucket: InsightBucket;
    item: StructuredInsight | undefined;
    emptyLabel: string;
  }[] = [
    {
      bucket: "critical",
      item: critF[0],
      emptyLabel: "No critical issues this run.",
    },
    {
      bucket: "opportunity",
      item: oppF[0],
      emptyLabel: "No opportunities this run.",
    },
    { bucket: "win", item: winF[0], emptyLabel: "No wins this run." },
  ];

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:items-stretch">
      {slots.map(({ bucket, item, emptyLabel }) => (
        <div key={bucket} className="flex min-h-[200px] flex-col">
          {item ? (
            <InsightDashboardCard
              insight={item}
              bucket={bucket}
              onDismiss={() => onDismiss(cardKey(bucket, item))}
            />
          ) : (
            <SpotlightEmptyBucket bucket={bucket} label={emptyLabel} />
          )}
        </div>
      ))}
    </div>
  );
}

function SpotlightEmptyBucket({ bucket, label }: { bucket: InsightBucket; label: string }) {
  const tone =
    bucket === "critical"
      ? "border-destructive/35 bg-destructive/[0.03]"
      : bucket === "opportunity"
        ? "border-amber-500/35 bg-amber-500/[0.04]"
        : "border-emerald-500/35 bg-emerald-500/[0.04]";
  return (
    <div
      className={cn(
        "flex min-h-[200px] flex-1 flex-col items-center justify-center rounded-xl border border-dashed px-4 py-8 text-center text-xs leading-relaxed text-muted-foreground",
        tone,
      )}
    >
      {label}
    </div>
  );
}

function InsightsColumn({
  tabAll,
  sectionLabel,
  emptyLabel,
  items,
  bucket,
  onDismiss,
}: {
  tabAll: boolean;
  sectionLabel: string;
  emptyLabel: string;
  items: StructuredInsight[];
  bucket: InsightBucket;
  onDismiss: (key: string) => void;
}) {
  if (items.length === 0) {
    return (
      <section>
        {tabAll && (
          <h3 className="mb-2.5 flex flex-wrap items-center gap-x-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span>{sectionLabel}</span>
            <span className="font-normal opacity-70">·</span>
            <span className="font-normal opacity-90">0 items</span>
          </h3>
        )}
        <div className="rounded-lg border border-dashed bg-muted/20 p-4 text-center text-xs text-muted-foreground">
          {emptyLabel}
        </div>
      </section>
    );
  }

  return (
    <section>
      {tabAll && (
        <h3 className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span>{sectionLabel}</span>
          <span className="font-normal opacity-70">·</span>
          <span className="font-normal opacity-90">{items.length} items</span>
        </h3>
      )}
      <div className="grid gap-4 sm:gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <InsightDashboardCard
            key={cardKey(bucket, item)}
            insight={item}
            bucket={bucket}
            onDismiss={() => onDismiss(cardKey(bucket, item))}
          />
        ))}
      </div>
    </section>
  );
}

// ---------- platform + severity (StructuredInsight cards) ---------------

function PlatformGlyph({ platform }: { platform: string }) {
  if (platform === "google") return <GoogleIcon className="size-3.5 shrink-0" />;
  if (platform === "meta") return <MetaIcon className="size-3.5 shrink-0" />;
  return (
    <span className="inline-flex h-4 shrink-0 items-center rounded bg-muted px-1.5 text-[10px] font-medium uppercase text-muted-foreground">
      {platform}
    </span>
  );
}

interface SeverityTone {
  border: string;
  bg: string;
  label: string;
}

/**
 * Severity → colour for card chrome. Expect `critical` | `opportunity` | `win`.
 * Legacy `warning` → opportunity styling. Legacy severity `amplify` → win.
 */
function severityTone(severity: string): SeverityTone {
  switch (severity) {
    case "critical":
      return {
        border: "border-l-4 border-l-destructive border-y border-r",
        bg: "bg-destructive/[0.03]",
        label: "text-destructive",
      };
    case "opportunity":
    case "warning":
      return {
        border: "border-l-4 border-l-amber-500 border-y border-r",
        bg: "bg-amber-500/[0.04]",
        label: "text-amber-700 dark:text-amber-400",
      };
    case "win":
    case "amplify":
      return {
        border: "border-l-4 border-l-emerald-500 border-y border-r",
        bg: "bg-emerald-500/[0.04]",
        label: "text-emerald-700 dark:text-emerald-400",
      };
    default:
      return {
        border: "border",
        bg: "",
        label: "text-muted-foreground",
      };
  }
}

// ---------- insight dashboard card (rich layout) ------------------------

function splitImpactAndBody(diagnosis: string): { rest: string; impact: string | null } {
  const m = diagnosis.match(
    /(?:^|\n)(?:\*\*)?Potential impact:?(?:\*\*)?\s*([^\n]+)/i,
  );
  if (!m) return { rest: diagnosis, impact: null };
  return {
    rest: diagnosis.replace(m[0], "\n").trim(),
    impact: m[1].trim(),
  };
}

/**
 * If the model emits `ISSUE:` / `EVIDENCE:` / `ACTION:` blocks (optionally
 * bold), surface them as distinct rows; otherwise the whole string is a
 * single body.
 */
function parseIssueEvidenceAction(text: string): {
  issue?: string;
  evidence?: string;
  action?: string;
} {
  const out: { issue?: string; evidence?: string; action?: string } = {};
  const lines = text.split("\n");
  let current: "issue" | "evidence" | "action" | null = null;
  const buf: string[] = [];
  const flush = () => {
    if (current && buf.length) {
      out[current] = buf.join("\n").trim();
      buf.length = 0;
    }
  };
  for (const line of lines) {
    const h = line.match(/^\*?\*?(ISSUE|EVIDENCE|ACTION)\*?\*?:\s*(.*)$/i);
    if (h) {
      flush();
      current = h[1].toLowerCase() as "issue" | "evidence" | "action";
      if (h[2]) buf.push(h[2]);
    } else if (current) {
      buf.push(line);
    }
  }
  flush();
  return out;
}

/** Uppercase pill on the right — tint guessed from category wording (matches marketing-ref screenshots). */
function categoryPillClasses(category: string): string {
  const c = category.toLowerCase();
  if (/budget|spend|allocation/i.test(c)) {
    return "border-rose-500/35 bg-rose-500/10 text-rose-800 dark:text-rose-200";
  }
  if (/bid|tcpa|troas|cpa target|strategy/i.test(c)) {
    return "border-orange-500/40 bg-orange-500/10 text-orange-800 dark:text-orange-200";
  }
  if (/conversion|quality|creative|signal|performance/i.test(c)) {
    return "border-emerald-500/35 bg-emerald-500/12 text-emerald-900 dark:text-emerald-200";
  }
  return "border-border bg-muted/60 text-muted-foreground";
}

function bucketFallbackPillClasses(bucket: InsightBucket): string {
  switch (bucket) {
    case "critical":
      return "border-destructive/35 bg-destructive/10 text-destructive";
    case "opportunity":
      return "border-amber-500/35 bg-amber-500/10 text-amber-900 dark:text-amber-200";
    case "win":
      return "border-emerald-500/35 bg-emerald-500/12 text-emerald-900 dark:text-emerald-200";
    default:
      return "border-border bg-muted/60 text-muted-foreground";
  }
}

function bucketFallbackLabel(bucket: InsightBucket): string {
  switch (bucket) {
    case "critical":
      return "Needs attention";
    case "opportunity":
      return "Opportunity";
    case "win":
      return "Working well";
    default:
      return "Insight";
  }
}

/** Prefer headline + supporting body like the reference UI (bold CPI line + paragraph). */
function splitHeadlineAndRest(text: string): { headline: string; rest: string | null } {
  const t = text.trim();
  if (!t) return { headline: "", rest: null };
  const para = t.split(/\n\n+/);
  if (para.length >= 2 && para[0].length <= 360) {
    return { headline: para[0].trim(), rest: para.slice(1).join("\n\n").trim() };
  }
  const sentence = t.match(/^(.{16,320}?[.!?])(\s+)([\s\S]{20,})$/);
  if (sentence?.[1] && sentence[3]) {
    return { headline: sentence[1].trim(), rest: sentence[3].trim() };
  }
  return { headline: t, rest: null };
}

function WhyParagraph({ text }: { text: string }) {
  return (
    <p className="mt-2 text-[13px] leading-snug text-muted-foreground">
      <span className="italic text-muted-foreground/90">Why:</span>{" "}
      <span>{text}</span>
    </p>
  );
}

function InsightDashboardCard({
  insight,
  bucket,
  onDismiss,
}: {
  insight: StructuredInsight;
  bucket: InsightBucket;
  onDismiss: () => void;
}) {
  const diagnosis = insight.diagnosis ?? "";
  const { rest: diagSansImpact, impact: lineImpact } = splitImpactAndBody(diagnosis);

  const apiIssue = insight.issue?.trim() ?? "";
  const apiEvidence = insight.evidence?.trim() ?? "";
  const apiAction = insight.action?.trim() ?? "";
  const hasApiRows = !!(apiIssue || apiEvidence || apiAction);

  let issueText: string | undefined;
  let evidenceText: string | undefined;
  let actionText: string | undefined;
  if (hasApiRows) {
    issueText = apiIssue || undefined;
    evidenceText = apiEvidence || undefined;
    actionText = apiAction || undefined;
  } else {
    const parsed = parseIssueEvidenceAction(diagSansImpact);
    issueText = parsed.issue;
    evidenceText = parsed.evidence;
    actionText = parsed.action;
  }

  const hasStructuredRows = Boolean(issueText || evidenceText || actionText);
  const tone = severityTone(
    bucket === "critical" ? "critical" : bucket === "opportunity" ? "opportunity" : "win",
  );

  const scopeTrim = insight.scope?.trim() ?? "";
  const catTrim = insight.category?.trim() ?? "";
  const useScopeAsPill = bucket === "opportunity" && scopeTrim;
  const pillLabel = useScopeAsPill ? scopeTrim : catTrim || bucketFallbackLabel(bucket);
  const pillClasses = useScopeAsPill
    ? categoryPillClasses(scopeTrim)
    : catTrim
      ? categoryPillClasses(catTrim)
      : bucketFallbackPillClasses(bucket);

  const unstructured = splitHeadlineAndRest(diagSansImpact);

  return (
    <article
      className={cn(
        "flex h-full min-h-0 flex-col overflow-hidden rounded-xl border bg-background shadow-sm",
        tone.border,
        tone.bg,
      )}
    >
      <div className="flex flex-1 flex-col p-3.5 pb-3 sm:p-4">
        <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-2">
          <PlatformGlyph platform={insight.platform} />
          <span
            className={cn(
              "shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              pillClasses,
            )}
          >
            {pillLabel}
          </span>
        </div>

        <p className="mt-2 text-[17px] font-semibold leading-snug tracking-tight text-foreground">
          {insight.entity_name}
        </p>

        {scopeTrim && !useScopeAsPill ? (
          <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {scopeTrim}
          </p>
        ) : null}

        {lineImpact ? (
          <p className="mt-2 text-[11px] text-muted-foreground">
            <span className="text-muted-foreground/85">Potential impact: </span>
            <span className="font-medium text-foreground">{lineImpact}</span>
          </p>
        ) : null}

        <div className="mt-3 flex-1 space-y-2.5">
          {hasStructuredRows ? (
            <>
              {issueText ? <DiagnosisBlock k="ISSUE" text={issueText} /> : null}
              {evidenceText ? <DiagnosisBlock k="EVIDENCE" text={evidenceText} /> : null}
              {actionText ? <DiagnosisBlock k="ACTION" text={actionText} /> : null}
            </>
          ) : unstructured.rest ? (
            <>
              <p className="text-[17px] font-semibold leading-snug text-foreground">{unstructured.headline}</p>
              <WhyParagraph text={unstructured.rest} />
            </>
          ) : (
            <p className="text-[17px] font-semibold leading-snug text-foreground">{unstructured.headline}</p>
          )}
        </div>

        <button
          type="button"
          className="mt-4 self-end text-[11px] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          onClick={onDismiss}
        >
          Dismiss
        </button>
      </div>
    </article>
  );
}

function DiagnosisBlock({ k, text }: { k: "ISSUE" | "EVIDENCE" | "ACTION"; text: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/90">
        {k}
      </p>
      <p className="mt-1 text-[13px] leading-snug text-foreground/95">{text}</p>
    </div>
  );
}

// ---------- analysis panel & fallbacks -----------------------------------

/**
 * Collapsible "Analysis" preamble. Default-collapsed because the cards
 * below are the primary content; the prose is supporting context for
 * users who want it.
 */
function AnalysisPanel({ summary }: { summary: string }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-lg border bg-muted/30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium text-foreground/80 hover:bg-muted/40"
      >
        <span className="inline-flex items-center gap-1.5">
          <Sparkles className="size-3.5 text-violet-500" strokeWidth={2.5} />
          Analysis
        </span>
        {open ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
      </button>
      {open && (
        <div className="border-t px-3 pb-3 pt-2">
          <Prose className="prose-sm max-w-none">
            <ReactMarkdown
              remarkPlugins={[RemarkGFM]}
              rehypePlugins={[[RehypeExternalLinks, { target: "_blank", rel: "noopener noreferrer" }]]}
            >
              {summary}
            </ReactMarkdown>
          </Prose>
        </div>
      )}
    </section>
  );
}

/**
 * The agent produced useful prose but nothing fit the structured schema.
 * We surface the prose as the primary content and a tiny badge that
 * explains why there are no cards.
 */
function DataSummaryFallback({ summary }: { summary: string }) {
  return (
    <div className="space-y-2.5">
      <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
        Structured cards unavailable for this run
      </Badge>
      <Prose className="prose-sm max-w-none">
        <ReactMarkdown
          remarkPlugins={[RemarkGFM]}
          rehypePlugins={[[RehypeExternalLinks, { target: "_blank", rel: "noopener noreferrer" }]]}
        >
          {summary}
        </ReactMarkdown>
      </Prose>
    </div>
  );
}

/**
 * Always-available "Show full analysis" toggle — surfaces the raw
 * markdown the agent produced. Useful as both a fallback and a
 * copy-paste affordance for users who want to share the run.
 */
function FullAnalysisToggle({ content }: { content: string }) {
  const [open, setOpen] = useState(false);
  if (!content?.trim()) return null;
  return (
    <section className="rounded-lg border border-dashed">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-9 w-full justify-between rounded-lg px-3 text-xs font-medium text-muted-foreground hover:bg-muted/40"
        onClick={() => setOpen((v) => !v)}
      >
        <span>{open ? "Hide full analysis" : "Show full analysis"}</span>
        {open ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
      </Button>
      {open && (
        <div className="border-t px-3 pb-3 pt-2">
          <Prose className="prose-sm max-w-none">
            <ReactMarkdown
              remarkPlugins={[RemarkGFM]}
              rehypePlugins={[[RehypeExternalLinks, { target: "_blank", rel: "noopener noreferrer" }]]}
            >
              {content}
            </ReactMarkdown>
          </Prose>
        </div>
      )}
    </section>
  );
}

/**
 * Stable React key for a StructuredInsight card. Falls back to the
 * concatenation of the fields most likely to be unique across a single
 * response. The `prefix` distinguishes sections when the same entity
 * legitimately appears twice.
 */
function cardKey(prefix: string, item: StructuredInsight): string {
  return `${prefix}:${item.platform}:${item.entity_name}:${item.scope ?? ""}:${item.category}`;
}

// ---------- skeleton -----------------------------------------------------

export function InsightsResultSkeleton() {
  return (
    <div className="mx-auto w-full max-w-[1280px] space-y-5">
      <div className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
        <div className="border-b border-border/60 px-4 py-4 sm:px-5">
          <div className="flex items-center gap-2">
            <Sparkles className="size-5 shrink-0 animate-pulse text-violet-500" strokeWidth={2} />
            <Skeleton className="h-5 w-64" />
          </div>
          <Skeleton className="mt-2 h-3 w-full max-w-md" />
          <p className="mt-3 text-xs text-muted-foreground">Running cross-platform analysis… ~10–15s.</p>
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <Skeleton className="h-7 w-14 rounded-full" />
            <Skeleton className="h-7 w-24 rounded-full" />
            <Skeleton className="h-7 w-32 rounded-full" />
            <Skeleton className="h-7 w-20 rounded-full" />
          </div>
        </div>
        <div className="p-4 sm:p-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <Skeleton className="h-56 w-full rounded-xl" />
            <Skeleton className="h-56 w-full rounded-xl" />
            <Skeleton className="h-56 w-full rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}
